import './SelectInput.scss';

import { mdiClose } from '@mdi/js';
import { Select } from 'antd';
import type { StyleSize } from 'common/models';
import Icon from 'components/Icon/Icon';
import { removeDiacritics } from 'helpers/validation';
import React, { useCallback, useEffect, useMemo } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import type { InputNoteConfig } from '../_common';
import {
  getHookFieldClass,
  getHookGroupClass,
  renderErrorMessage,
  renderHookInputHeader
} from '../_common';
import SelectItemAdditionalInfo from '../SelectItemAdditionalInfo';

interface SelectInputProps {
  name: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  options?: RegisterOptions;
  popupRootClassName?: string;
  disabled?: boolean;
  loading?: boolean;
  mode?: 'multiple' | 'tags';
  items: SelectItemWithDescription[];
  showGroupedOptions?: boolean;
  groupedOptions?: JSX.Element[];
  hideWhenNotNeeded?: boolean;
  popupRender?: (menu: React.ReactElement) => React.ReactElement;
  inputNote?: InputNoteConfig;
  size?: StyleSize;
  hideErrorMessage?: boolean;
  customOnChange?: (newValue: any, oldValue: any) => void;
  hideLabel?: boolean;
  className?: string;
}

export interface SelectItemWithDescription {
  id: string | number | boolean;
  name: string;
  description?: React.ReactNode | string | null;
  disabled?: boolean;
  additionalInfo?: string;
}

const { Option } = Select;

const SelectInput = ({
  name,
  label,
  placeholder,
  required,
  options,
  popupRootClassName,
  disabled,
  loading,
  mode,
  items,
  groupedOptions,
  showGroupedOptions,
  hideWhenNotNeeded,
  popupRender,
  inputNote,
  size = 'medium',
  hideErrorMessage,
  customOnChange,
  hideLabel,
  className
}: SelectInputProps) => {
  const { control, getFieldState, formState, setValue } = useFormContext();

  const { invalid, isDirty, error, isTouched } = getFieldState(name, formState);

  const showError = !!error && isDirty && !hideErrorMessage;

  useEffect(() => {
    if (hideWhenNotNeeded) {
      if (items.length === 1) {
        setValue(name, items[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customTagRender = useCallback(
    (tagProps: any) => {
      const { label, value, closable, onClose } = tagProps;

      const selectedOption = items.find((x: SelectItemWithDescription) => x.id === value);

      return (
        <span className="ant-select-selection-item">
          <span className="ant-select-selection-item-content">
            <div>
              <div>
                {selectedOption?.description
                  ? `${selectedOption.name} - ${selectedOption.description}`
                  : label}
                {selectedOption?.additionalInfo && (
                  <SelectItemAdditionalInfo additionalInfo={selectedOption.additionalInfo} />
                )}
              </div>
            </div>
          </span>
          <span className="ant-select-selection-item-remove" aria-hidden="true">
            <span role="img" aria-label="close" className="anticon anticon-close">
              <Icon path={mdiClose} onClick={closable && onClose} />
            </span>
          </span>
        </span>
      );
    },
    [items]
  );

  const preparedItems = useMemo(() => {
    return showGroupedOptions
      ? groupedOptions || []
      : items.map((d) => (
          <Option
            title={d.name}
            key={d.id.toString()}
            value={d.id}
            label={
              <span>
                {d.description ? `${d.name} - ${d.description}` : d.name}{' '}
                {d.additionalInfo && <SelectItemAdditionalInfo additionalInfo={d.additionalInfo} />}
              </span>
            }
            disabled={d.disabled}
          >
            <div className="Option_name">
              {d.name}
              {d.additionalInfo && <SelectItemAdditionalInfo additionalInfo={d.additionalInfo} />}
            </div>
            <div className="Option_description">{d.description}</div>
          </Option>
        ));
  }, [groupedOptions, items, showGroupedOptions]);

  const resolvedPopupClassName = popupRootClassName;

  if (hideWhenNotNeeded) {
    if (items.length === 1 || (items.length === 0 && !required)) {
      return <div />;
    }
  }

  return (
    <div
      className={getHookGroupClass(
        label,
        `HookSelectInput ${size}${className ? ` ${className}` : ''}`
      )}
    >
      {!hideLabel && renderHookInputHeader(name, label, required, inputNote)}
      <Controller
        name={name}
        control={control}
        rules={options}
        render={({ field }) => {
          return (
            <Select
              {...field}
              id={name}
              placeholder={placeholder}
              className={getHookFieldClass(isTouched, invalid)}
              optionLabelProp="label"
              getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
              classNames={{ popup: { root: resolvedPopupClassName || 'HookSelectInputDropdown' } }}
              disabled={disabled}
              loading={loading}
              mode={mode}
              allowClear={!required}
              onChange={(value) => {
                if (customOnChange) {
                  customOnChange(value ?? null, field.value);
                } else {
                  field.onChange(value ?? null);
                }
              }}
              showSearch={{
                filterOption: (input, option) =>
                  removeDiacritics(option?.title?.toString())
                    .toLowerCase()
                    .indexOf(removeDiacritics(input).toLowerCase()) >= 0
              }}
              tagRender={customTagRender}
              popupRender={popupRender}
            >
              {preparedItems}
            </Select>
          );
        }}
      />
      {renderErrorMessage(showError, error?.message)}
    </div>
  );
};

export default SelectInput;
